import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { errorReporter } from "~/error-handling/errorReporter";

/**
 * Custom hook that automatically tracks navigation breadcrumbs for all route changes.
 * This provides a centralized way to monitor navigation throughout the application.
 *
 * This hook should be used once at the application level (e.g., in App.tsx or MainLayout)
 * to capture all navigation events automatically.
 *
 * @example
 * ```tsx
 * // In your main app component or layout
 * function App() {
 *   useNavigationBreadcrumbs();
 *   return <Router>...</Router>;
 * }
 * ```
 */
export const useNavigationBreadcrumbs = () => {
  const location = useLocation();
  const previousLocationRef = useRef<string>(
    location.pathname + location.search + location.hash,
  );

  useEffect(() => {
    const previousLocation = previousLocationRef.current;
    const currentLocation = location.pathname + location.search + location.hash;

    // Only add breadcrumb if the location actually changed
    if (previousLocation !== currentLocation) {
      // Use full location (with query params and hash) for more detailed tracking
      errorReporter.addNavigationBreadcrumb(previousLocation, currentLocation);
      previousLocationRef.current = currentLocation;
    }
  }, [location.pathname, location.search, location.hash]);
};
